/**
 * Utilities to transform analytics API data into the format expected by report components
 */

// Standard dimension mapping from backend
const DEFAULT_DIMENSION_MAPPING = {
  'strategy_capability': 'Strategy & Capability',
  'infrastructure_data': 'Infrastructure & Data',
  'initiatives_operations': 'Initiatives & Operations',
  'value_realisation': 'Value Realisation'
};

/**
 * Transform analytics response data to match ReusableReportLayout expected format
 */
export function transformAnalyticsToReportFormat(analyticsData, scope = 'organization') {
  const { summary, insights, privacy_notice } = analyticsData;
  const dimensionMetadata = summary?.dimension_metadata || DEFAULT_DIMENSION_MAPPING;
  
  // Use report_data directly from backend (now built from individual responses)
  const reportData = summary?.report_data || {};
  
  // Ensure all expected dimensions exist in reportData
  const allExpectedDimensions = ['strategy_capability', 'infrastructure_data', 'initiatives_operations', 'value_realisation'];
  allExpectedDimensions.forEach(dimensionId => {
    if (!reportData[dimensionId]) {
      reportData[dimensionId] = [];
    }
  });

  // Calculate dimension summaries from report_data (using new priority fields)
  const dimensionSummaries = allExpectedDimensions.map(dimensionId => {
    const data = reportData[dimensionId] || [];
    const validData = data.filter(item => item.score > 0); // Filter out items with score <= 0
    
    if (validData.length === 0) {
      return { 
        dimension: dimensionId, 
        avgScore: 0.00, 
        avgImportance: 0.00, 
        avgGap: 0.00, 
        axisCount: 0 
      };
    }
    
    const avgScore = validData.reduce((sum, item) => sum + item.score, 0) / validData.length;
    // Filter out null/undefined importance values for accurate averaging
    const validImportanceData = validData.filter(item => item.importance != null);
    const avgImportance = validImportanceData.length > 0
      ? validImportanceData.reduce((sum, item) => sum + item.importance, 0) / validImportanceData.length
      : 0.0;
    const avgGap = validData.reduce((sum, item) => sum + (item.unmet_need || 0), 0) / validData.length;
    
    return {
      dimension: dimensionId,
      avgScore: Math.round(avgScore * 10) / 10, // 1 decimal place to match original
      avgImportance: Math.round(avgImportance * 10) / 10,
      avgGap: Math.round(avgGap * 10) / 10,
      axisCount: validData.length
    };
  });

  return {
    dimensionSummaries,
    reportData,
    dimensionMapping: dimensionMetadata,
    questionPriorities: summary?.question_priorities || [],
    analysis: insights ? {
      // Handle both old backend format and new ReportAnalysisAgent format
      executive_summary: insights.executive_summary || insights.analysis || '',
      key_strengths: insights.key_strengths || insights.key_findings || [],
      recommended_actions: insights.recommended_actions || insights.recommendations || [],
      improvement_areas: insights.improvement_areas || insights.weaknesses || [],
      detected_themes: insights.detected_themes || [],
      overall_maturity_level: insights.overall_maturity_level || (summary?.average_score ? getMaturityLevel(summary.average_score) : null),
      priority_question_insights: insights.priority_question_insights || {},
      analysis_generated_at: insights.analysis_generated_at,
      response_count: insights.response_count,
      session_count: insights.session_count
    } : null,
    privacyNotice: privacy_notice,
    summary: {
      totalResponses: summary?.total_responses || 0,
      uniqueUsers: summary?.unique_users || 0,
      uniqueCompanies: summary?.unique_companies || null,
      dateRange: summary?.date_range || '',
      averageScore: summary?.average_score || 0,
      sampleSize: summary?.sample_size || 0
    }
  };
}

/**
 * Get maturity level based on average score
 */
function getMaturityLevel(averageScore) {
  if (averageScore >= 4.0) return 'Advanced';
  if (averageScore >= 3.0) return 'Intermediate';
  if (averageScore >= 2.0) return 'Developing';
  return 'Beginning';
}

/**
 * Generate cache key for analytics data
 */
export function generateCacheKey(scope, filters) {
  const { company, industry, period, startDate, endDate } = filters;
  
  if (scope === 'organization') {
    return `analytics:org:${company}|${period}|${startDate || ''}|${endDate || ''}`;
  }
  
  if (scope === 'industry') {
    const industryKey = industry || 'ALL';
    return `analytics:ind:${industryKey}|${period}|${startDate || ''}|${endDate || ''}`;
  }
  
  return `analytics:${scope}:${period}|${startDate || ''}|${endDate || ''}`;
}

/**
 * Cache management with TTL and versioning
 */
const CACHE_VERSION = 1;
const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

export function setCacheWithTTL(key, data) {
  try {
    const cacheEntry = {
      version: CACHE_VERSION,
      timestamp: Date.now(),
      data: data
    };
    localStorage.setItem(key, JSON.stringify(cacheEntry));
    return true;
  } catch (error) {
    console.warn('Cache write failed:', error);
    return false;
  }
}

export function getCacheWithTTL(key) {
  try {
    const cached = localStorage.getItem(key);
    if (!cached) return null;
    
    const cacheEntry = JSON.parse(cached);
    
    // Check version
    if (cacheEntry.version !== CACHE_VERSION) {
      localStorage.removeItem(key);
      return null;
    }
    
    // Check TTL
    const now = Date.now();
    const age = now - cacheEntry.timestamp;
    if (age > CACHE_TTL_MS) {
      localStorage.removeItem(key);
      return null;
    }
    
    return cacheEntry.data;
  } catch (error) {
    console.warn('Cache read failed:', error);
    try {
      localStorage.removeItem(key);
    } catch (cleanupError) {
      // Ignore cleanup errors
    }
    return null;
  }
}

/**
 * Check if analytics data has AI analysis generated
 */
export function hasAnalysisGenerated(analyticsData) {
  return !!(analyticsData?.insights?.analysis && analyticsData.insights.analysis.trim() !== '');
}

/**
 * Transform personal session data to unified layout format
 * Backend returns deduplicated responses (one per question, newest answer)
 */
export function transformSessionToReportFormat(sessionData, analysis = null) {
  const { meta } = sessionData;
  const { report_data } = meta;
  const dimensionMapping = sessionData.dimension_metadata || DEFAULT_DIMENSION_MAPPING;

  // Backend returns deduplicated responses - just count them
  const totalUniqueResponses = (sessionData.responses || []).length;

  // Calculate dimension summaries from report_data - using established summary logic
  const allExpectedDimensions = ['strategy_capability', 'infrastructure_data', 'initiatives_operations', 'value_realisation'];
  const dimensionSummaries = allExpectedDimensions.map(dimensionId => {
    const data = report_data[dimensionId] || [];
    const validData = data.filter(item => item.score > 0); // Filter out items with score <= 0
    
    if (validData.length === 0) {
      return { 
        dimension: dimensionId, 
        avgScore: 0.00, 
        avgImportance: 0.00, 
        avgGap: 0.00, 
        axisCount: 0 
      };
    }
    
    const avgScore = validData.reduce((sum, item) => sum + item.score, 0) / validData.length;
    // Filter out null/undefined importance values for accurate averaging
    const validImportanceData = validData.filter(item => item.importance != null);
    const avgImportance = validImportanceData.length > 0
      ? validImportanceData.reduce((sum, item) => sum + item.importance, 0) / validImportanceData.length
      : 0.0;
    const avgGap = validData.reduce((sum, item) => sum + item.gap, 0) / validData.length;
    
    return {
      dimension: dimensionId,
      avgScore: Math.round(avgScore * 10) / 10, // 1 decimal place to match original
      avgImportance: Math.round(avgImportance * 10) / 10,
      avgGap: Math.round(avgGap * 10) / 10,
      axisCount: validData.length
    };
  });

  return {
    dimensionSummaries,
    reportData: report_data,
    dimensionMapping,
    analysis: analysis,
    privacyNotice: null,
    summary: {
      totalResponses: totalUniqueResponses,
      uniqueUsers: 1,
      uniqueCompanies: null,
      dateRange: '',
      averageScore: dimensionSummaries.reduce((sum, dim) => sum + dim.avgScore, 0) / dimensionSummaries.length,
      sampleSize: totalUniqueResponses
    },
    sessionData // Pass through for QuestionsSection
  };
}

/**
 * Create mock session data format from analytics data (for compatibility)
 */
export function createMockSessionData(analyticsData) {
  const transformed = transformAnalyticsToReportFormat(analyticsData);

  return {
    meta: {
      report_data: transformed.reportData
    },
    dimension_metadata: transformed.dimensionMapping,
    // No responses for analytics (this is what differentiates it from personal)
    responses: null
  };
}

// =============================================================================
// Adoption Curve Helpers
// TODO: Extract to Cosmos DB config (see GitHub issue #33)
// =============================================================================

/**
 * Map AI Evaluation report sections to scorecard dimensions
 */
const EVALUATION_SECTION_TO_DIMENSION = {
  'Tools & Processes': 'initiatives_operations',
  'Skills & Capability Development': 'strategy_capability',
  'Culture & Risk Readiness': 'infrastructure_data',
  'Opportunities & Friction': 'value_realisation'
};

/**
 * Extract the overall assessment text from AI Evaluation report
 * Returns the "Current State of AI" section content (<150 words)
 */
export function extractOverallAssessment(executiveSummary) {
  if (!executiveSummary) return null;

  // Look for "Current State of AI" section
  const currentStateMatch = executiveSummary.match(/###?\s*Current State of AI\s*\n+([\s\S]*?)(?=\n###|\n---|\*\*Priority|$)/i);
  if (currentStateMatch && currentStateMatch[1]) {
    const content = currentStateMatch[1].trim();
    return truncateToWords(content, 150);
  }

  // Fallback: try to get first paragraph if no section found
  const firstParagraph = executiveSummary.split('\n\n')[0];
  if (firstParagraph && !firstParagraph.startsWith('#')) {
    return truncateToWords(firstParagraph, 150);
  }

  return null;
}

/**
 * Extract per-dimension insights from AI Evaluation report
 * Returns object { dimension_id: "insight text" }
 */
export function extractDimensionInsights(executiveSummary) {
  if (!executiveSummary) return {};

  const insights = {};

  for (const [sectionName, dimensionId] of Object.entries(EVALUATION_SECTION_TO_DIMENSION)) {
    const sectionRegex = new RegExp(
      `###?\\s*${sectionName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*\\n+([\\s\\S]*?)(?=\\n###|\\n---|$)`,
      'i'
    );
    const sectionMatch = executiveSummary.match(sectionRegex);

    if (sectionMatch && sectionMatch[1]) {
      const sectionContent = sectionMatch[1].trim();

      // Try "Summary" subsection first
      const summaryMatch = sectionContent.match(/\*\*Summary\*\*\s*\n+([\s\S]*?)(?=\n\*\*|$)/i);
      if (summaryMatch && summaryMatch[1]) {
        insights[dimensionId] = cleanInsightText(summaryMatch[1], 150);
        continue;
      }

      // Try "Top Insights" subsection
      const insightsMatch = sectionContent.match(/\*\*Top Insights\*\*\s*\n+([\s\S]*?)(?=\n\*\*|$)/i);
      if (insightsMatch && insightsMatch[1]) {
        insights[dimensionId] = cleanInsightText(insightsMatch[1], 150);
        continue;
      }

      // Fallback: first 150 words of section
      insights[dimensionId] = cleanInsightText(sectionContent, 150);
    }
  }

  return insights;
}

/**
 * Clean and truncate insight text
 */
function cleanInsightText(text, maxWords = 150) {
  if (!text) return '';

  let cleaned = text
    .replace(/^\s*[-*]\s*/gm, '• ')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/\*([^*]+)\*/g, '$1')
    .replace(/`([^`]+)`/g, '$1')
    .trim();

  return truncateToWords(cleaned, maxWords);
}

/**
 * Truncate text to max words
 */
function truncateToWords(text, maxWords) {
  const words = text.split(/\s+/);
  if (words.length > maxWords) {
    return words.slice(0, maxWords).join(' ') + '...';
  }
  return text;
}

/**
 * Get adoption stage label for a score
 * TODO: Extract thresholds to config (see GitHub issue #33)
 */
export function getAdoptionStageLabel(score) {
  if (score >= 4.2) return 'Innovators';
  if (score >= 3.4) return 'Early Adopters';
  if (score >= 2.6) return 'Early Majority';
  if (score >= 1.8) return 'Late Majority';
  return 'Laggards';
}
